import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { fetchPublicCmsConfig } from '../utils/cmsConfigCache';

const CookieBar = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // If already consented to all, inject analytics
      const parsed = JSON.parse(consent);
      if (parsed.type === 'all') {
        injectAnalytics();
      }
    }
  }, []);

  // Mark body so other fixed elements (e.g. floating contact button) can lift
  // themselves above the cookie bar while it is visible.
  useEffect(() => {
    if (visible) {
      document.body.setAttribute('data-cookie-bar', 'visible');
      window.dispatchEvent(new CustomEvent('cookie-bar-visibility', { detail: { visible: true } }));
    } else {
      document.body.removeAttribute('data-cookie-bar');
      window.dispatchEvent(new CustomEvent('cookie-bar-visibility', { detail: { visible: false } }));
    }
  }, [visible]);

  const injectAnalytics = async () => {
    try {
      const data = await fetchPublicCmsConfig();
      const settings = data?.settings || {};
      const gaId = settings.analyticsId;
      if (gaId && !document.getElementById('ga-script')) {
        const script1 = document.createElement('script');
        script1.id = 'ga-script';
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
        document.head.appendChild(script2);
      }
      // Inject custom head code
      const customCode = settings.customHeadCode;
      if (customCode && !document.getElementById('custom-head-code')) {
        const div = document.createElement('div');
        div.id = 'custom-head-code';
        div.innerHTML = customCode;
        const scripts = div.querySelectorAll('script');
        scripts.forEach(s => {
          const ns = document.createElement('script');
          if (s.src) ns.src = s.src;
          else ns.textContent = s.textContent;
          document.head.appendChild(ns);
        });
      }
    } catch {
      // Silently fail - analytics is non-critical
    }
  };

  const accept = (type) => {
    localStorage.setItem('cookie_consent', JSON.stringify({ type, date: new Date().toISOString() }));
    setVisible(false);
    // Notify listeners (AnalyticsTracker, ThemeApplier / Google Analytics) that consent changed
    try {
      window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: { type } }));
    } catch (e) {}
    if (type === 'all') {
      injectAnalytics();
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-bar"
      className="fixed bottom-0 left-0 right-0 z-[100] cookie-bar-enter"
    >
      <div className="bg-slate-900/95 backdrop-blur-lg border-t border-slate-700 text-white px-4 py-4 md:px-8">
        <div className="container mx-auto flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Shield className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium mb-1">Tento web pouziva cookies</p>
              <p className="text-xs text-slate-400">
                Pouzivame cookies pro analyzu navstevnosti a zlepseni vasich zkusenosti. 
                Vice informaci naleznete v nasich zasadach ochrany soukromi.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="cookie-reject-btn"
              onClick={() => accept('essential')}
              className="px-4 py-2 text-xs font-medium text-slate-300 hover:text-white border border-slate-600 rounded-lg hover:border-slate-500 transition-colors"
            >
              Pouze nutne
            </button>
            <button
              data-testid="cookie-accept-btn"
              onClick={() => accept('all')}
              className="px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors shadow-lg"
            >
              Prijmout vse
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBar;
